import * as React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { checkedCircleIconSmall } from "../../common/checkedCircleIcon";
import { VideoFlowStatusLine } from "../Sites/SiteAboutPage/VideoFlowStatusLine";
import { triStateIcon } from "../../common/triStateIcon";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { createApi } from "../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { timeSinceConvertor } from "../Sites/SiteAboutPage/SiteAboutPageUtils/timeSinceConvertor";
import { pointsCreator } from "../Sites/SiteAboutPage/SiteAboutPageUtils/pointsCreator";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import RunCircleIcon from "@mui/icons-material/RunCircle";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  ResposneObject,
  IgnoreZone,
  SiteCamerasResult,
} from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { createCameraSettingsIcon } from "../Sites/SiteAboutPage/SiteAboutPageUtils/createCameraSettingsIcon";
import { createCameraViewIcon } from "../Sites/SiteAboutPage/SiteAboutPageUtils/createCameraViewIcon";
import { universalAPIErrorHandler } from "../../utils/universalAPIErrorHandler";
import { resizeImage } from "../Sites/SiteAboutPage/SiteAboutPageUtils/resizeImage";
import { Tooltip } from "@mui/material";
import { integrationsMatcher } from "../../utils/integrationsMatcher";
import { addIgnoreLabel } from "../Sites/SiteAboutPage/SiteAboutPageUtils/addIgnoreLabel";
import { isChmOnlyIgnoreZones } from "../Sites/SiteAboutPage/SiteAboutPageUtils/isChmOnlyIgnoreZones";
import { useSiteAboutStore } from "../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { IgnoreZonesTable } from "../Sites/SiteAboutPage/IgnoreZonesTable";
import { MetadataTagZonesTable } from "../Sites/SiteAboutPage/MetadataTagZonesTable";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { MetadataTagZone } from "../EditIMetadataTagZones/EditMetadataTagZonesUtils/editIMetadataTagZonesInterfaces";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { safeSetItem } from "../../utils/localStorageUtils";
import { LineCrossingsOverlay } from "../Sites/SiteAboutPage/LineCrossingsOverlay";
import { useFlexIgnoreZonesCameraRow } from "../Sites/SiteAboutPage/useFlexIgnoreZonesCameraRow";
import { IgnoreZonePreviewHoverLayer } from "../../common/ignoreZoneHover/IgnoreZonePreviewHoverLayer";
import { handleTriggerMotionAxis } from "./EditCameraUtils/handleTriggerMotionAxis";
import { formatFrameRate } from "./EditCameraUtils/formatFrameRate";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const CameraInfoRowEditCamera = ({
  obj,
  expanded,
  setBackdropLoadingScreenOn,
  setEditCameraAlerts,
  siteInfo,
  permissions,
  srcImages,
}: {
  obj: SiteCamerasResult;
  expanded: boolean;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setEditCameraAlerts: Dispatcher<any>;
  siteInfo: any;
  permissions: any;
  srcImages: any;
}) => {
  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";
  const isAxisCamera =
    (obj as { camera_type?: { display_name?: string } }).camera_type
      ?.display_name === "Axis";
  const [isChmOnlyIgnoreZonesCamera, setIsChmOnlyIgnoreZonesCamera] =
    React.useState(false);
  const [initialRowDataLoaded, setInitialRowDataLoaded] = React.useState(false);
  const hideCameraIgnoreZonesPanel = isChmOnlyIgnoreZonesCamera;

  const [metadataTagZones, setMetadataTagZones] = React.useState<
    MetadataTagZone[]
  >([]);

  const sentinelVerifierSite =
    integrationsMatcher("Name", siteInfo.integration_type) ===
    "Sentinel Verifier"
      ? true
      : false;

  const aiLinkSite =
    integrationsMatcher("Name", siteInfo.integration_type) === "AI Link"
      ? true
      : false;

  const {
    allIgnoreZones,
    setAllIgnoreZones,
    visibleIgnoreZones,
    setVisibleIgnoreZones,
  } = useSiteAboutStore();

  const {
    lineCrossingActive,
    lineCrossingsData,
    lineCrossingsMetricLabels,
    setShowSlicingWarningMessage,
  } = useEditCameraStore();

  const editCameraText = (
    <>
      {permissions.edit_cameras ? (
        <>
          <EditIcon fontSize="small" />
          &nbsp;Edit Camera
        </>
      ) : (
        <>Full Camera Info</>
      )}
    </>
  );

  const theme = createTheme({
    palette: {
      primary: {
        main: "#FF8400",
      },
    },
  });
  const shrinkLineCrossingsTabs = useMediaQuery("(max-width:1575px)");
  const lineCrossingsTabsWidth = shrinkLineCrossingsTabs ? "305px" : "375px";

  const navigate = useNavigate();

  const [clickedTab, setClickedTab] = React.useState("one");

  const [responseObject, setResponseObject] = React.useState<ResposneObject>({
    // null until /camera/{id}/status/ answers, so an unloaded or failed row
    // shows "No data yet" and not a green tick it never earned (ENG-1782)
    connected: null,
    frame_since_launch: null,
    good_frame: null,
    blur_metric: 0,
    native_frame_rate: 0,
    frame_dimensions: "",
    last_motion: 0,
    preview_url: "",
    health_status: "",
    is_blurry: null,
    aspect_ratio: "",
    comments: "",
    camera_view: "",
    aspect_ratio_status: "unknown",
    frame_rate_status: "unknown",
    resolution_status: "unknown",
    camera_health_comments: "",
    camera_name: "",
    camera_start_time: 0,
    camera_view_comments: "",
    camera_view_status: "unknown",
    codec_status: "unknown",
    display_frame_since_launch: true,
    dynamic_slicing: false,
    id: 0,
    overall_status: "unknown",
  });

  const aPICall = async (
    navigate: NavigateFunction,
    id: string,
    setResponseObject: Dispatcher<ResposneObject>,
    setAllIgnoreZones: (allIgnoreZones: IgnoreZone[]) => void,
    setVisibleIgnoreZones: (visibleIgnoreZones: IgnoreZone[]) => void
  ): Promise<void> => {
    const AxiosUI = createApi(``);
    setIsChmOnlyIgnoreZonesCamera(false);

    const retryFunction = () => {
      void aPICall(
        navigate,
        id,
        setResponseObject,
        setAllIgnoreZones,
        setVisibleIgnoreZones
      );
    };

    try {
      const statusResponse: AxiosResponse = await AxiosUI.get(`/camera/${id}/status/`);
      const frameDimensions = statusResponse.data.frame_dimensions?.match(/\d+/g);
      if (frameDimensions?.some((dim: string) => parseInt(dim, 10) > 1080)) {
        setShowSlicingWarningMessage(true);
      }
      setResponseObject(statusResponse.data);
      localStorage.setItem(
        "preview_url",
        JSON.stringify(statusResponse.data.preview_url)
      );

      const [ignoreZonesResult, tagZonesResult] = await Promise.allSettled([
        AxiosUI.get(`/camera/${obj.id}/ignore_zones/`),
        AxiosUI.get(`tagzone/?camera=${id}`),
      ]);

      if (ignoreZonesResult.status === "fulfilled") {
        const ignoreZonesResponse = ignoreZonesResult.value;
        setIsChmOnlyIgnoreZonesCamera(
          isChmOnlyIgnoreZones(ignoreZonesResponse.data || [])
        );
        const labeledZones = addIgnoreLabel(ignoreZonesResponse.data);
        setAllIgnoreZones(labeledZones);
        setVisibleIgnoreZones(labeledZones);
        localStorage.setItem("ignoreZones", JSON.stringify(labeledZones));
      } else {
        universalAPIErrorHandler(
          ignoreZonesResult.reason as AxiosError,
          navigate,
          retryFunction,
          () => {}
        );
      }

      if (tagZonesResult.status === "fulfilled") {
        setMetadataTagZones(tagZonesResult.value.data.results);
      } else {
        universalAPIErrorHandler(
          tagZonesResult.reason as AxiosError,
          navigate,
          retryFunction,
          () => {}
        );
      }
    } catch (reason) {
      universalAPIErrorHandler(reason as AxiosError, navigate, retryFunction, () => {
        setShowSlicingWarningMessage(false);
      }
      );
    }
  };

  React.useEffect(() => {
    if (!obj.id) {
      return;
    }
    let mounted = true;
    setBackdropLoadingScreenOn(true);
    setInitialRowDataLoaded(false);
    void aPICall(
      navigate,
      obj.id.toString(),
      setResponseObject,
      setAllIgnoreZones,
      setVisibleIgnoreZones
    ).finally(() => {
      if (!mounted) return;
      setInitialRowDataLoaded(true);
      setBackdropLoadingScreenOn(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per selected camera initial load
  }, [obj.id, navigate, setAllIgnoreZones, setBackdropLoadingScreenOn, setVisibleIgnoreZones]);

  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;

    setDimensions(resizeImage(naturalWidth, naturalHeight));
  };

  const { flags } = useFeatureFlag();
  const viewEditMetadataTagZonesPage = flags.viewEditMetadataTagZonesPage;
  const showLineCrossings = flags.showLineCrossings;

  const {
    flexIzActive,
    zonesForPreview,
    zonesForColorSidebar,
    ignoreZonesTitleText,
    ignoreZonesHeadingClassName,
    flexPresetSelectControl,
    flexPresetDialog,
  } = useFlexIgnoreZonesCameraRow({
    siteInfo,
    obj,
    expanded,
    visibleIgnoreZones,
    allIgnoreZones,
  });

  const ignoreZoneHoverEnabled =
    !hideCameraIgnoreZonesPanel &&
    zonesForPreview.length > 0 &&
    ((!autopatrolSite && clickedTab !== "four") ||
      (autopatrolSite && clickedTab === "one"));

  const showLineCrossingTabs =
    showLineCrossings &&
    (lineCrossingActive.intruder || lineCrossingActive.vehicle);

  React.useEffect(() => {
    if (hideCameraIgnoreZonesPanel && showLineCrossingTabs) {
      setClickedTab("four");
    }
  }, [hideCameraIgnoreZonesPanel, showLineCrossingTabs]);

  const cameraTimingSummaryRow = !autopatrolSite ? (
    <div className="mt-[8px] flex flex-col gap-y-2 pt-[8px]">
      <div
        className="h-px w-[200px] max-w-full shrink-0 bg-gray-200"
        aria-hidden
      />
      <div className="w-full min-w-0">
        <div className="font-mulish font-bold text-[11px] leading-tight text-black">
          Last Motion Time
        </div>
        <div className="font-mulish text-[11px] leading-snug text-black">
          {responseObject.last_motion
            ? timeSinceConvertor(Number(responseObject.last_motion))
            : "--"}
        </div>
      </div>
      <div className="w-full min-w-0">
        <div className="font-mulish font-bold text-[11px] leading-tight text-black">
          Camera Start Time
        </div>
        <div className="font-mulish text-[11px] leading-snug text-black">
          {responseObject.camera_start_time
            ? timeSinceConvertor(Number(responseObject.camera_start_time))
            : "--"}
        </div>
      </div>
      {srcImages ? (
        <div className="w-full min-w-0">
          <div className="font-mulish font-bold text-[11px] leading-tight text-black">
            Camera View
          </div>
          <div className="font-mulish text-[11px] leading-snug text-black">
            {createCameraViewIcon(responseObject, srcImages)}
          </div>
        </div>
      ) : null}
      {!hideCameraIgnoreZonesPanel && (
        <div
          className="h-px w-[200px] max-w-full shrink-0 bg-gray-200"
          aria-hidden
        />
      )}
    </div>
  ) : null;

  const cameraSettingsBlock = !autopatrolSite ? (
    <div>
      <div className="font-mulish text-[16px] text-actuate-blue pt-[16px]">
        Camera Settings
      </div>
      <div className="font-mulish text-[12px] text-black pt-[10px] flex">
        <div>
          {createCameraSettingsIcon(
            responseObject.aspect_ratio_status,
            "Aspect Ratio",
            responseObject.comments,
            responseObject.camera_view,
            responseObject
          )}
          &nbsp;&nbsp;Aspect Ratio
        </div>
        <div className="font-mulish font-bold text-[12px] text-black pl-[28px]">
          {responseObject.aspect_ratio ? responseObject.aspect_ratio : "N/A"}
        </div>
      </div>
      <div className="font-mulish text-[12px] text-black pt-[10px] flex">
        <div>
          {createCameraSettingsIcon(
            responseObject.resolution_status,
            "Resolution",
            responseObject.comments,
            responseObject.camera_view,
            responseObject
          )}
          &nbsp;&nbsp;Resolution
        </div>
        <div className="font-mulish font-bold text-[12px] text-black pl-[36px]">
          {responseObject.frame_dimensions
            ? responseObject.frame_dimensions
            : "N/A"}
        </div>
      </div>
      {!sentinelVerifierSite && !aiLinkSite && (
        <div>
          <div className="font-mulish text-[12px] text-black pt-[10px] flex">
            <div>
              {createCameraSettingsIcon(
                responseObject.frame_rate_status,
                "Frame Rate",
                responseObject.comments,
                responseObject.camera_view,
                responseObject
              )}
              &nbsp;&nbsp;Frame Rate
            </div>
            <div className="font-mulish font-bold text-[12px] text-black pl-[32px]">
              {formatFrameRate(responseObject.native_frame_rate)}
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  if (!initialRowDataLoaded) {
    return null;
  }

  const triggerMotionButton = isAxisCamera ? (
    <div className="pt-[10px]">
      <OrangeTextButton
        onClick={() => {
          setBackdropLoadingScreenOn(true);
          handleTriggerMotionAxis(
            String(obj.id),
            navigate,
            setBackdropLoadingScreenOn,
            setEditCameraAlerts
          );
        }}
        text={
          <>
            <RunCircleIcon />
            &nbsp;Trigger Motion on Camera
          </>
        }
        disabled={false}
      />
    </div>
  ) : null;

  return obj.id ? (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 0 }}>
            <div className="relative flex min-h-[420px] items-start gap-[15px] pt-5 pb-4">
              {!window.location.href.includes("editcamera") && (
                <div className="absolute top-[10px] right-[0px] w-[160px]">
                  <OrangeTextButton
                    onClick={() => {
                      setBackdropLoadingScreenOn(true);
                      localStorage.setItem(
                        "currentCameraId",
                        JSON.stringify(obj.id)
                      );
                      localStorage.setItem("currentCamera", obj.camera_name);
                      // Store only essential camera info to avoid localStorage quota issues
                      safeSetItem("currentCameraInfo", {
                        id: obj.id,
                        camera_name: obj.camera_name,
                      });
                      localStorage.setItem(
                        "preview_url",
                        JSON.stringify(responseObject.preview_url)
                      );
                      localStorage.setItem(
                        "ignoreZones",
                        JSON.stringify(allIgnoreZones)
                      );
                      navigate(`/sites/${siteInfo.id}/editcamera/${obj.id}`);
                    }}
                    text={editCameraText}
                    disabled={false}
                  />
                </div>
              )}
              {siteInfo.integration_type_name !== "ailink" && (
                <div className="order-2 w-[380px] shrink-0">
                  <div className="font-mulish text-[16px] text-actuate-blue">
                    General Information
                  </div>
                  {siteInfo.integration_type_name !== "SMTP_per_camera" &&
                    !autopatrolSite && (
                      <VideoFlowStatusLine
                        siteId={siteInfo.id}
                        cameraId={obj.id}
                        connected={responseObject.connected}
                      />
                    )}
                  <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                    {triStateIcon(
                      responseObject.is_blurry === null
                        ? null
                        : !responseObject.is_blurry
                    )}
                    &nbsp;&nbsp;Blur Metric
                  </div>
                  <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                    {triStateIcon(responseObject.good_frame)}
                    &nbsp;&nbsp;Stream Stability
                  </div>
                  {!autopatrolSite && responseObject.display_frame_since_launch && (
                    <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                      {triStateIcon(responseObject.frame_since_launch)}
                      &nbsp;&nbsp;Frame Since Launch
                    </div>
                  )}
                  {responseObject.dynamic_slicing && (
                    <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                      {checkedCircleIconSmall}
                      &nbsp;&nbsp;Dynamic Slicing
                    </div>
                  )}
                  {cameraTimingSummaryRow}
                  {!hideCameraIgnoreZonesPanel && cameraSettingsBlock}
                </div>
              )}
              {siteInfo.integration_type_name === "ailink" &&
                cameraTimingSummaryRow && (
                  <div className="order-2 w-[380px] shrink-0">
                    {cameraTimingSummaryRow}
                  </div>
                )}
              {!autopatrolSite && (
                <div className="order-3 min-h-[300px] w-[375px] shrink-0">
                  {hideCameraIgnoreZonesPanel && cameraSettingsBlock}
                  {hideCameraIgnoreZonesPanel && showLineCrossingTabs && (
                    <div className="w-[375px] pt-[12px]">
                      <div className="font-mulish text-[18px] text-actuate-blue">
                        Line Crossings
                      </div>
                      <div className="pt-[8px]">
                        <OrangeTextButton
                          onClick={() => {
                            setBackdropLoadingScreenOn(true);
                            navigate(
                              `/sites/${siteInfo.id}/editcamera/${obj.id}/editlinecrossings`
                            );
                          }}
                          text={
                            <>
                              <EditIcon fontSize="small" />
                              &nbsp;Edit Line Crossings
                            </>
                          }
                          disabled={false}
                        />
                      </div>
                      <div className="relative mt-[8px] w-[375px] max-h-[220px] overflow-auto">
                        {lineCrossingsData.length > 0 ? (
                          lineCrossingsData.map((line, index) => {
                            const displayLabels: string[] = [];
                            const hasIntruder = line.metric_labels.some(
                              (id) =>
                                lineCrossingsMetricLabels.find(
                                  (m) => m.id === id
                                )?.label_name === "intruder_line_crossing"
                            );
                            const hasVehicle = line.metric_labels.some(
                              (id) =>
                                lineCrossingsMetricLabels.find(
                                  (m) => m.id === id
                                )?.label_name === "vehicle_line_crossing"
                            );
                            const hasBike = line.metric_labels.some(
                              (id) =>
                                lineCrossingsMetricLabels.find(
                                  (m) => m.id === id
                                )?.label_name === "bike_line_crossing"
                            );

                            if (hasIntruder) displayLabels.push("Intruder");
                            if (hasVehicle || hasBike)
                              displayLabels.push("Vehicle/Bike");

                            const metricsText =
                              displayLabels.length > 0
                                ? displayLabels.join(", ")
                                : "No metrics";

                            return (
                              <div key={`line-lc-${line.id || index}`}>
                                <div
                                  style={{
                                    height: 20,
                                    width: 6,
                                    background: `${line.color}`,
                                  }}
                                  className="absolute"
                                />
                                <p className="font-mulish text-[14px] indent-5">
                                  {metricsText}
                                </p>
                                &nbsp;
                              </div>
                            );
                          })
                        ) : (
                          <p className="font-mulish text-[14px] text-gray-500">
                            No line crossings configured
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {!hideCameraIgnoreZonesPanel && !showLineCrossingTabs && (
                    <div className="flex w-[375px] flex-col">
                      <div className="flex items-start gap-2">
                        <p className={ignoreZonesHeadingClassName}>
                          {ignoreZonesTitleText}
                        </p>
                        <div className="shrink-0">
                          <OrangeTextButton
                            onClick={() => {
                              setBackdropLoadingScreenOn(true);
                              localStorage.setItem(
                                "currentCameraId",
                                JSON.stringify(obj.id)
                              );
                              localStorage.setItem(
                                "currentCamera",
                                obj.camera_name
                              );
                              safeSetItem("currentCameraInfo", {
                                id: obj.id,
                                camera_name: obj.camera_name,
                              });
                              localStorage.setItem(
                                "preview_url",
                                JSON.stringify(responseObject.preview_url)
                              );
                              localStorage.setItem(
                                "ignoreZones",
                                JSON.stringify(allIgnoreZones)
                              );
                              navigate(
                                `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                              );
                            }}
                            text={
                              <Tooltip
                                title={"Edit Ignore Zones"}
                                placement="top"
                                sx={{ fontSize: "20px", fontFamily: "mulish" }}
                              >
                                <EditIcon />
                              </Tooltip>
                            }
                            disabled={false}
                          />
                        </div>
                      </div>
                      {flexPresetSelectControl}
                      <div className="mt-[4px] max-h-[220px] min-h-[150px] w-full overflow-auto">
                        <div className="w-full">
                          {zonesForColorSidebar.map(
                            (object: IgnoreZone, zi: number) => (
                              <div
                                key={`${obj.id}-sidebar-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                                className="relative"
                              >
                                <div
                                  style={{
                                    height: 20,
                                    width: 6,
                                    background: `${object.line_color}`,
                                  }}
                                  className="absolute"
                                />
                                <p className="font-mulish text-[14px] indent-5">
                                  {object.label_display_name}
                                </p>
                                &nbsp;
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {!hideCameraIgnoreZonesPanel && showLineCrossingTabs && (
                    <div className="relative w-[375px]">
                      <div className="font-mulish text-[18px] text-actuate-blue flex">
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <ThemeProvider theme={theme}>
                            <Tabs
                              value={clickedTab}
                              onChange={(event, tabValue) => {
                                setClickedTab(tabValue);
                              }}
                              aria-label="intestigation_tabs"
                              color="primary"
                              sx={{ width: lineCrossingsTabsWidth }}
                            >
                              <Tab
                                value="one"
                                label={ignoreZonesTitleText}
                                sx={{
                                  fontFamily: "Mulish",
                                  fontWeight: "Bold",
                                  color: "primary",
                                  fontSize: "16px",
                                }}
                              />
                              <Tab
                                value="four"
                                label={`Line Crossings`}
                                sx={{
                                  fontFamily: "Mulish",
                                  fontWeight: "Bold",
                                  color: "primary",
                                  fontSize: "16px",
                                }}
                              />
                            </Tabs>
                          </ThemeProvider>
                        </Box>
                      </div>
                      {clickedTab === "one" && (
                        <div className="absolute pt-[5px]">
                          <OrangeTextButton
                            onClick={() => {
                              setBackdropLoadingScreenOn(true);
                              navigate(
                                `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                              );
                            }}
                            text={
                              <>
                                <EditIcon fontSize="small" />
                                &nbsp;Edit Ignore Zones
                              </>
                            }
                            disabled={false}
                          />
                        </div>
                      )}
                      {clickedTab === "one" && (
                        <div className="relative left-[0px] top-[50px] w-[375px]">
                          {flexPresetSelectControl}
                          <div className="h-[250px] w-full overflow-auto pt-[10px]">
                            {zonesForColorSidebar.map(
                              (object: IgnoreZone, zi: number) => (
                                <div
                                  key={`${obj.id}-lc-sidebar-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                                  className="relative"
                                >
                                  <div
                                    style={{
                                      height: 20,
                                      width: 6,
                                      background: `${object.line_color}`,
                                    }}
                                    className="absolute"
                                  />
                                  <p className="font-mulish text-[14px] indent-5">
                                    {object.label_display_name}
                                  </p>
                                  &nbsp;
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                      {clickedTab === "four" && (
                        <div className="absolute pt-[5px]">
                          <OrangeTextButton
                            onClick={() => {
                              setBackdropLoadingScreenOn(true);
                              navigate(
                                `/sites/${siteInfo.id}/editcamera/${obj.id}/editlinecrossings`
                              );
                            }}
                            text={
                              <>
                                <EditIcon fontSize="small" />
                                &nbsp;Edit Line Crossings
                              </>
                            }
                            disabled={false}
                          />
                        </div>
                      )}
                      {clickedTab === "four" && (
                        <div className="relative left-[0px] top-[50px] w-[375px] h-[130px] overflow-auto">
                          {lineCrossingsData.length > 0 ? (
                            lineCrossingsData.map((line, index) => {
                              const displayLabels: string[] = [];
                              const hasIntruder = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.label_name === "intruder_line_crossing"
                              );
                              const hasVehicle = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.label_name === "vehicle_line_crossing"
                              );
                              const hasBike = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.label_name === "bike_line_crossing"
                              );

                              if (hasIntruder) displayLabels.push("Intruder");
                              if (hasVehicle || hasBike)
                                displayLabels.push("Vehicle/Bike");

                              const metricsText =
                                displayLabels.length > 0
                                  ? displayLabels.join(", ")
                                  : "No metrics";

                              return (
                                <div key={`line-${line.id || index}`}>
                                  <div
                                    style={{
                                      height: 20,
                                      width: 6,
                                      background: `${line.color}`,
                                    }}
                                    className="absolute"
                                  />
                                  <p className="font-mulish text-[14px] indent-5">
                                    {metricsText}
                                  </p>
                                  &nbsp;
                                </div>
                              );
                            })
                          ) : (
                            <p className="font-mulish text-[14px] text-gray-500">
                              No line crossings configured
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {autopatrolSite &&
                viewEditMetadataTagZonesPage &&
                !hideCameraIgnoreZonesPanel && (
                <div className="order-3 w-[400px] shrink-0">
                  <div className="font-mulish text-[18px] text-actuate-blue flex">
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <ThemeProvider theme={theme}>
                        <Tabs
                          value={clickedTab}
                          onChange={(event, tabValue) => {
                            setClickedTab(tabValue);
                          }}
                          aria-label="intestigation_tabs"
                          color="primary"
                          sx={{ width: "345px" }}
                        >
                          <Tab
                            value="one"
                            label={ignoreZonesTitleText}
                            sx={{
                              fontFamily: "Mulish",
                              fontWeight: "Bold",
                              color: "primary",
                              fontSize: "16px",
                            }}
                          />
                          <Tab
                            value="two"
                            label={`Metadata Tag Zones`}
                            sx={{
                              fontFamily: "Mulish",
                              fontWeight: "Bold",
                              color: "primary",
                              fontSize: "16px",
                            }}
                          />
                        </Tabs>
                      </ThemeProvider>
                    </Box>
                  </div>
                  <div>
                    {clickedTab === "one" && (
                      <>
                        <br />
                        {flexPresetSelectControl}
                        <IgnoreZonesTable
                          visibleIgnoreZones={
                            flexIzActive ? zonesForPreview : visibleIgnoreZones
                          }
                          onEditClick={() => {
                            setBackdropLoadingScreenOn(true);
                            navigate(
                              `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                            );
                          }}
                        />
                      </>
                    )}
                    {clickedTab === "two" && (
                      <MetadataTagZonesTable
                        metadataTagZones={metadataTagZones}
                        onEditClick={() => {
                          setBackdropLoadingScreenOn(true);
                          navigate(
                            `/sites/${siteInfo.id}/editcamera/${obj.id}/editmetadatatagzones`
                          );
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {autopatrolSite &&
                !viewEditMetadataTagZonesPage &&
                !hideCameraIgnoreZonesPanel && (
                  <div className="order-3 flex min-h-[300px] w-[320px] shrink-0 flex-col">
                    <div className="flex items-start gap-2">
                      <p className={ignoreZonesHeadingClassName}>
                        {ignoreZonesTitleText}
                      </p>
                      <div className="shrink-0">
                        <OrangeTextButton
                          onClick={() => {
                            setBackdropLoadingScreenOn(true);
                            localStorage.setItem(
                              "currentCameraId",
                              JSON.stringify(obj.id)
                            );
                            localStorage.setItem(
                              "currentCamera",
                              obj.camera_name
                            );
                            safeSetItem("currentCameraInfo", {
                              id: obj.id,
                              camera_name: obj.camera_name,
                            });
                            localStorage.setItem(
                              "preview_url",
                              JSON.stringify(responseObject.preview_url)
                            );
                            localStorage.setItem(
                              "ignoreZones",
                              JSON.stringify(allIgnoreZones)
                            );
                            navigate(
                              `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                            );
                          }}
                          text={
                            <Tooltip
                              title={"Edit Ignore Zones"}
                              placement="top"
                              sx={{ fontSize: "20px", fontFamily: "mulish" }}
                            >
                              <EditIcon />
                            </Tooltip>
                          }
                          disabled={false}
                        />
                      </div>
                    </div>
                    {flexPresetSelectControl}
                    <div className="mt-[4px] max-h-[200px] min-h-[150px] w-[305px] overflow-auto">
                      <div className="w-[275px]">
                        {zonesForColorSidebar.map(
                          (object: IgnoreZone, zi: number) => (
                            <div
                              key={`${obj.id}-sidebar-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                              className="relative"
                            >
                              <div
                                style={{
                                  height: 20,
                                  width: 6,
                                  background: `${object.line_color}`,
                                }}
                                className="absolute"
                              />
                              <p className="font-mulish text-[14px] indent-5">
                                {object.label_display_name}
                              </p>
                              &nbsp;
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}
              {siteInfo.integration_type_name !== "ailink" ? (
                <div className="order-1 shrink-0 flex flex-col">
                  <IgnoreZonePreviewHoverLayer
                    className="relative"
                    zones={zonesForPreview}
                    enabled={ignoreZoneHoverEnabled}
                  >
                  <img
                    src={responseObject.preview_url}
                    style={{
                      width: dimensions ? `${dimensions.width}px` : "auto",
                      height: dimensions ? `${dimensions.height}px` : "auto",
                    }}
                    onLoad={handleImageLoad}
                    alt="TestCameraPhoto"
                  />
                  {/* Render ignore zones when NOT on Line Crossings tab */}
                  {!autopatrolSite &&
                    !hideCameraIgnoreZonesPanel &&
                    clickedTab !== "four" &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                      return (
                        <div
                          className="absolute top-[0px]"
                          key={`${obj.id}-iz-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                        >
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                  {/* Render line crossings when on Line Crossings tab */}
                  {!autopatrolSite &&
                    dimensions &&
                    lineCrossingsData.length > 0 &&
                    (clickedTab === "four" ||
                      (hideCameraIgnoreZonesPanel && showLineCrossingTabs)) && (
                      <LineCrossingsOverlay
                        lines={lineCrossingsData}
                        width={dimensions.width}
                        height={dimensions.height}
                        frameDimensions={responseObject.frame_dimensions}
                      />
                    )}
                  {autopatrolSite &&
                    !hideCameraIgnoreZonesPanel &&
                    clickedTab === "one" &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                      return (
                        <div
                          className="absolute top-[0px]"
                          key={`${obj.id}-iz-ap-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                        >
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                  {autopatrolSite &&
                    clickedTab === "two" &&
                    metadataTagZones.map((object: any) => {
                      return (
                        <div className="absolute top-[0px]" key={object.label}>
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                  </IgnoreZonePreviewHoverLayer>
                  {triggerMotionButton}
                </div>
              ) : (
                <div className="order-1 shrink-0 flex flex-col">
                  <IgnoreZonePreviewHoverLayer
                    className="relative"
                    zones={zonesForPreview}
                    enabled={ignoreZoneHoverEnabled}
                  >
                  <img
                    src={responseObject.preview_url}
                    onLoad={handleImageLoad}
                    style={{
                      width: dimensions ? `${dimensions.width}px` : "auto",
                      height: dimensions ? `${dimensions.height}px` : "auto",
                    }}
                    alt="TestCameraPhoto"
                  />
                  {!hideCameraIgnoreZonesPanel &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                    return (
                      <div
                        className="absolute top-[0px]"
                        key={`${obj.id}-iz-ailink-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                      >
                        {object.coordinates.map(
                          (array: number[], index: number) => {
                            return (
                              <svg
                                className="absolute top-[0px]"
                                key={array[0]}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            );
                          }
                        )}
                      </div>
                    );
                  })}
                  </IgnoreZonePreviewHoverLayer>
                  {triggerMotionButton}
                </div>
              )}
            </div>
            {!hideCameraIgnoreZonesPanel && flexPresetDialog}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  ) : (
    <></>
  );
};
